import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, ToggleInput } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchCargoTypeActions as FetchActions,
  UpdateCargoTypeActions as UpdateActions,
} from 'store/ducks/cargoTypes';
import { UpdateCargoTypeValidator } from 'validators/CargoTypes';
import * as S from './styles';

interface Props {
  typeId: string | number;
  onUpdate?: () => void;
}

export const TypeUpdateForm: React.FC<Props> = ({ typeId, onUpdate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { data: cargoType } = useSelector(
    (state: RootState) => state.fetchCargoType
  );

  const { loading: updatingCargoType, validationErrors } = useSelector(
    (state: RootState) => state.updateCargoType
  );

  const fetchCargoType = useCallback(() => {
    dispatch(FetchActions.request(typeId));
  }, [dispatch, typeId]);

  const onCargoTypeLoad = useCallback((): void => {
    if (!cargoType) return;
    const { name, blockedAt } = cargoType;

    formRef.current?.setData({
      name,
      blockedAt: blockedAt ? '1' : '0',
    });
  }, [cargoType]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateCargoTypeValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(typeId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess, typeId]
  );

  useEffect(() => {
    fetchCargoType();
  }, [fetchCargoType]);

  useEffect(() => {
    onCargoTypeLoad();
  }, [onCargoTypeLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Input name="name" label="Nome" />
          <ToggleInput name="blockedAt" label="Bloqueado" />
        </S.FormRow>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/tipos-cargas">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {updatingCargoType ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
